// HistoricalMonitoring.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import dayjs from "dayjs";
import { Card, CircularProgress, Stack } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDDatePicker from "components/MDDatePicker";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { historicalSchema } from "./historicalSchema";
import { fetchHistoricalData } from "./fetchHistoricalData";

// register once
ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// static options outside render
const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: "time",
      time: { unit: "hour" },
      title: {
        display: true,
        text: i18n.t("trafficMonitoring.historical.axisTime"),
        color: "#fff", // X-axis title in white
        font: { size: 14 },
      },
      ticks: {
        color: "#fff", // X-axis tick labels in white
        font: { size: 12 },
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: i18n.t("trafficMonitoring.historical.axisValue"),
        color: "#fff", // Y-axis title in white
        font: { size: 14 },
      },
      ticks: {
        color: "#fff", // Y-axis tick labels in white
        font: { size: 12 },
        callback: (value) => {
          const num = Number(value);
          if (!Number.isFinite(num)) return "";
          return num.toFixed(1);
        },
      },
    },
  },
  plugins: {
    legend: {
      position: "top",
      labels: {
        color: "#fff", // Legend text in white
      },
    },
  },
};

const COLOR_PALETTE = {
  egnatia: "#eb5514",
  paralia: "#cc0036",
  periferiakos: "#E98500",
  tsimiski: "#0095b3",
  // "" :"#9b2226",
  // "" :"#f4a261",
  // "" :"#2a9d8f",
};
const COLOR_POOL = [
  "#f4a261",
  "#2a9d8f",
  "#6a994e",
  "#8338ec",
  "#264653",
  "#ffb703",
  "#8ecae6",
];

const GLOBAL_COLOR_CACHE = {};
let colorIndex = 0;

function getColor(key) {
  const normalized = key.trim().toLowerCase();
  if (COLOR_PALETTE[normalized]) return COLOR_PALETTE[normalized];

  if (!GLOBAL_COLOR_CACHE[normalized]) {
    GLOBAL_COLOR_CACHE[normalized] = COLOR_POOL[colorIndex % COLOR_POOL.length];
    colorIndex++;
  }

  return GLOBAL_COLOR_CACHE[normalized];
}
// tweak brightness of a hex color; lum ∈ [-1,1]
function adjustLuminance(hex, lum = 0) {
  hex = String(hex).replace(/[^0-9a-f]/gi, "");
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  let rgb = "#";
  for (let i = 0; i < 3; i++) {
    const part = parseInt(hex.substr(i * 2, 2), 16);
    const adjusted = Math.round(Math.min(Math.max(0, part + part * lum), 255));
    rgb += ("00" + adjusted.toString(16)).slice(-2);
  }
  return rgb;
}

// transformer unchanged
function transformGeneric(
  raw,
  { xField, yField, groupField, splitField, labelPrefix = "" }
) {
  const withDates = raw
    .map((d) => ({ ...d, __date: new Date(d[xField]) }))
    .sort((a, b) => a.__date - b.__date);

  const keys = Array.from(
    new Set(
      withDates.map((d) =>
        splitField
          ? `${d[groupField].trim().toLowerCase()}||${d[splitField]}`
          : d[groupField].trim().toLowerCase()
      )
    )
  );

  return {
    datasets: keys.map((key, idx) => {
      const [rawName, s] = splitField ? key.split("||") : [key, null];
      const g = rawName; // trimmed & lowercased
      const data = withDates
        .filter(
          (d) =>
            d[groupField].trim().toLowerCase() === g &&
            (!splitField || d[splitField] === s)
        )
        .map((d) => ({ x: d.__date, y: d[yField] }));
      // restore a human-friendly label if you want:
      // find a representative item to get original casing
      const originalEntry = withDates.find(
        (d) =>
          d[groupField].trim().toLowerCase() === g &&
          (!splitField || d[splitField] === s)
      );
      const displayName = originalEntry ? originalEntry[groupField].trim() : g;

      const label = labelPrefix
        ? `${labelPrefix} – ${displayName}${s ? ` (${s})` : ""}`
        : `${displayName}${s ? ` (${s})` : ""}`;

      const base = getColor(g); // base color from your palette

      const color = splitField
        ? s === "min"
          ? adjustLuminance(base, -0.4)
          : s === "max"
          ? adjustLuminance(base, +0.8)
          : base
        : base;

      // const isMain = !splitField || s === "avg"; // true for avg or no split
      const isMain = !splitField || s === "avg" || s === "weighted_avg";

      return {
        label,
        data,
        borderColor: color,
        backgroundColor: color + "33",
        pointRadius: 3,
        tension: 0.3,
        fill: false,
        pathKey: g, // used for grouping in legend
        isMain, // marks the "avg" line
      };
    }),
  };
}

export default function HistoricalMonitoring() {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(null); // Date | null
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // builds endpoint for custom vs default
  const buildEndpoint = useCallback((cfg, date) => {
    if (!date) return cfg.endpoint;
    const [base, qs = ""] = cfg.endpoint.split("?");
    const params = new URLSearchParams(qs);
    params.set("use_last_24h", "false");
    params.set("target_date", dayjs(date).format("YYYY-MM-DD"));
    return `${base}?${params.toString()}`;
  }, []);

  // central fetch & transform
  const loadCharts = useCallback(
    async (date) => {
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          historicalSchema.map(async (cfg) => {
            const url = buildEndpoint(cfg, date);
            const raw = await fetchHistoricalData(url);
            if (!Array.isArray(raw)) throw new Error(`Bad payload from ${url}`);
            return transformGeneric(raw, cfg);
          })
        );
        setCharts(results);
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [buildEndpoint]
  );

  // initial last-24h load
  useEffect(() => {
    loadCharts(null);
  }, [loadCharts]);

  // memoize controls: only re-renders when selectedDate changes
  const controls = useMemo(
    () => (
      <Stack direction="row" spacing={2} alignItems="center">
        <MDDatePicker
          input={{
            placeholder: "dd/mm/yyyy",
            sx: {
              color: "#344767",
              "&::placeholder": { color: "#eef1f6", opacity: 1 },
              "& input::placeholder": { color: "#eef1f6", opacity: 1 },
              "& .MuiInputBase-input": { color: "#eef1f6" },
            },
          }}
          options={{
            enableTime: false,
            dateFormat: "d/m/Y",
            altFormat: "d/m/Y",
            maxDate: new Date(),
          }}
          value={selectedDate}
          onChange={setSelectedDate}
        />
        <MDButton
          variant="gradient"
          color="info"
          disabled={!selectedDate}
          onClick={() => loadCharts(selectedDate)}
        >
          {t("common.buttons.apply")}
        </MDButton>
        <MDButton
          variant="gradient"
          color="light"
          onClick={() => {
            setSelectedDate(null);
            loadCharts(null);
          }}
        >
          {t("trafficMonitoring.historical.last24h")}
        </MDButton>
      </Stack>
    ),
    [selectedDate, loadCharts, t]
  );

  // memoize chart list: only re-renders when `charts` changes
  const chartList = useMemo(
    () => (
      <>
        {charts.map((data, idx) => {
          const cfg = historicalSchema[idx];
          const options = {
            ...CHART_OPTIONS,
            scales: {
              x: {
                ...CHART_OPTIONS.scales.x,
                title: {
                  ...CHART_OPTIONS.scales.x.title,
                  text: cfg.axiosXLabel || CHART_OPTIONS.scales.x.title.text,
                },
              },
              y: {
                ...CHART_OPTIONS.scales.y,
                title: {
                  ...CHART_OPTIONS.scales.y.title,
                  text: cfg.axiosYLabel || CHART_OPTIONS.scales.y.title.text,
                },
              },
            },
            plugins: {
              ...CHART_OPTIONS.plugins,
              legend: {
                position: "top",

                // generate one label per pathKey
                labels: {
                  ...CHART_OPTIONS.plugins.legend.labels,
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    const seen = {};
                    return ds.reduce((items, dset, idx) => {
                      const key = dset.pathKey;
                      if (!seen[key]) {
                        seen[key] = true;
                        // collect all series indices for this path
                        const indices = ds
                          .map((_, i) => (ds[i].pathKey === key ? i : -1))
                          .filter((i) => i >= 0);

                        const swatchSource =
                          indices.map((i) => ds[i]).find((d) => d.isMain) ||
                          ds[indices[0]];

                        items.push({
                          text: swatchSource.label.replace(
                            /\s*\(weighted_avg\)/i,
                            ""
                          ),
                          fillStyle: swatchSource.borderColor,
                          hidden: !chart.isDatasetVisible(indices[0]),
                          datasetIndices: indices, // stash for click handler
                        });
                      }
                      return items;
                    }, []);
                  },
                },

                // clicking a grouped label toggles all its series
                onClick: (e, legendItem, legend) => {
                  const chart = legend.chart;
                  legendItem.datasetIndices.forEach((i) => {
                    const vis = chart.isDatasetVisible(i);
                    chart.setDatasetVisibility(i, !vis);
                  });
                  chart.update();
                },
              },
            },
          };

          return (
            <Card key={cfg.id} sx={{ mb: 2, height: 400 }}>
              <MDBox p={2}>
                <MDTypography variant="h6">{cfg.name}</MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  flexGrow: 1,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <MDBox sx={{ flexGrow: 1 }}>
                  <Line data={data} options={options} />
                </MDBox>
              </MDBox>
            </Card>
          );
        })}
      </>
    ),
    [charts]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress size={50} color="primary" />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Card sx={{ mb: 2, p: 2 }}>{controls}</Card>
      {chartList}
    </DashboardLayout>
  );
}
